import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, FileUp, Search, Loader2, Download, Trash2, FileText, ShieldCheck,
  Copy, Layers, Link2, X, Upload,
} from "lucide-react";

const STATUS_META = {
  CARICATO: { label: "Caricato", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  ARCHIVIATO: { label: "Archiviato", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  ANNULLATO: { label: "Annullato", cls: "bg-red-100 text-red-700 border-red-200" },
};

const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function SignDocuments() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [linkedIdentity, setLinkedIdentity] = useState(null);
  const [idQuery, setIdQuery] = useState("");
  const [idResults, setIdResults] = useState([]);
  const [idSearching, setIdSearching] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const versionInputRef = useRef(null);
  const [versionTarget, setVersionTarget] = useState(null);

  const load = useCallback(async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/sign/documents", { params: { q: search } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setTitle(""); setFile(null); setLinkedIdentity(null);
    setIdQuery(""); setIdResults([]);
    setDialogOpen(true);
  };

  const searchIdentity = async () => {
    if (!idQuery.trim()) return;
    setIdSearching(true);
    try {
      const { data } = await api.get("/sign/identities", { params: { q: idQuery } });
      setIdResults(data);
      if (!data.length) toast.info("Nessuna identity trovata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setIdSearching(false);
    }
  };

  const displayIdentity = (it) =>
    [it.nome, it.cognome].filter(Boolean).join(" ") || it.ragione_sociale || it.identity_id;

  const onSelectFile = (f) => {
    if (!f) { setFile(null); return; }
    const isPdf = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) { toast.error("Sono ammessi solo file PDF"); return; }
    if (f.size > 25 * 1024 * 1024) { toast.error("File troppo grande (max 25 MB)"); return; }
    setFile(f);
  };

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file PDF"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      if (linkedIdentity) fd.append("identity_id", linkedIdentity.identity_id);
      await api.post("/sign/documents", fd);
      toast.success("Documento caricato");
      setDialogOpen(false);
      load(q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const onVersionFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !versionTarget) return;
    const docId = versionTarget;
    setVersionTarget(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/sign/documents/${docId}/versions`, fd);
      toast.success("Nuova versione aggiunta");
      load(q);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const triggerVersion = (docId) => {
    setVersionTarget(docId);
    setTimeout(() => versionInputRef.current?.click(), 0);
  };

  const download = async (docId, filename) => {
    try {
      const { data } = await api.get(`/sign/documents/${docId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename || `${docId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download non riuscito");
    }
  };

  const copyHash = async (h) => {
    try {
      await navigator.clipboard.writeText(h);
      toast.success("Impronta SHA-256 copiata");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = h; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); ta.remove();
        toast.success("Impronta SHA-256 copiata");
      } catch {
        toast.error("Copia non riuscita");
      }
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/sign/documents/${deleteTarget.document_id}`);
      toast.success("Documento eliminato");
      setDeleteTarget(null);
      load(q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sign-documents-page">
      <Header />
      <input ref={versionInputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onVersionFile} data-testid="sign-version-file-input" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button
          onClick={() => nav("/assietica-sign")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"
          data-testid="sign-doc-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Assietica Sign
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
              <FileText className="h-6 w-6" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-slate-900">Documenti</h1>
              <p className="text-sm text-slate-500">PDF originali immutabili, con impronta SHA-256 e versioni</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-new-document-btn">
            <FileUp className="h-4 w-4 mr-1.5" /> Carica documento
          </Button>
        </div>

        <div className="relative max-w-md mb-4">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q)}
            placeholder="Cerca per titolo, ID o nome file…"
            className="pl-10 bg-white"
            data-testid="sign-document-search"
          />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-400" data-testid="sign-documents-loading">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-slate-400" data-testid="sign-documents-empty">
              Nessun documento. Carica il primo PDF con "Carica documento".
            </div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="sign-documents-list">
              {items.map((d) => {
                const sm = STATUS_META[d.status] || STATUS_META.CARICATO;
                return (
                  <div key={d.document_id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-slate-50/60" data-testid={`sign-document-row-${d.document_id}`}>
                    <div className="h-10 w-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800 truncate">{d.title}</span>
                        <span className="font-mono text-xs text-slate-400">{d.document_id}</span>
                        <Badge variant="outline" className={sm.cls}>{sm.label}</Badge>
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <Layers className="h-3.5 w-3.5" /> v{d.current_version}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-emerald-700" title="Impronta di integrità SHA-256 del documento">
                        <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                        <span className="font-mono truncate">{d.sha256}</span>
                        <button onClick={() => copyHash(d.sha256)} className="text-slate-400 hover:text-slate-700 shrink-0" data-testid={`sign-copy-hash-${d.document_id}`}>
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <span className="text-slate-400 ml-1">· {fmtSize(d.size)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => download(d.document_id, d.original_filename)} data-testid={`sign-download-${d.document_id}`}>
                        <Download className="h-4 w-4 mr-1" /> Scarica
                      </Button>
                      <Button size="sm" variant="ghost" className="text-slate-500 hover:text-sky-600" onClick={() => triggerVersion(d.document_id)} data-testid={`sign-add-version-${d.document_id}`}>
                        <Upload className="h-4 w-4 mr-1" /> Versione
                      </Button>
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-500 hover:text-red-600" onClick={() => setDeleteTarget(d)} data-testid={`sign-doc-delete-${d.document_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Upload dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="sign-document-dialog">
          <DialogHeader>
            <DialogTitle>Carica documento</DialogTitle>
            <DialogDescription>Carica un PDF originale: verrà calcolata l'impronta SHA-256 e salvato come versione 1 immutabile.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">Titolo</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Contratto RC Professionale" data-testid="sign-doc-title" />
            </div>

            <div>
              <Label className="text-xs text-slate-500">File PDF</Label>
              <Input type="file" accept="application/pdf,.pdf" onChange={(e) => onSelectFile(e.target.files?.[0] || null)} data-testid="sign-doc-file" />
              {file && <p className="text-xs text-slate-400 mt-1">{file.name} · {fmtSize(file.size)}</p>}
            </div>

            <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-3">
              <Label className="text-xs text-slate-500 mb-1.5 block">Collega a un'identity (opzionale)</Label>
              {linkedIdentity ? (
                <div className="inline-flex items-center gap-1.5 text-xs text-sky-700">
                  <Link2 className="h-3.5 w-3.5" /> {displayIdentity(linkedIdentity)} ({linkedIdentity.identity_id})
                  <button type="button" onClick={() => setLinkedIdentity(null)} className="text-slate-400 hover:text-red-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <Input value={idQuery} onChange={(e) => setIdQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchIdentity())}
                      placeholder="Cerca cliente per nome, CF, ID…" className="bg-white h-9" data-testid="sign-doc-identity-search" />
                    <Button type="button" variant="outline" className="h-9" onClick={searchIdentity} disabled={idSearching} data-testid="sign-doc-identity-search-btn">
                      {idSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                  {idResults.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
                      {idResults.map((it) => (
                        <button key={it.identity_id} type="button" onClick={() => { setLinkedIdentity(it); setIdResults([]); setIdQuery(""); }}
                          className="w-full text-left px-3 py-2 hover:bg-sky-50 text-sm" data-testid={`sign-doc-identity-result-${it.identity_id}`}>
                          <div className="font-medium text-slate-800">{displayIdentity(it)}</div>
                          <div className="text-xs text-slate-400">{[it.codice_fiscale, it.email].filter(Boolean).join(" · ")}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="sign-doc-cancel">Annulla</Button>
            <Button onClick={upload} disabled={uploading} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="sign-doc-upload">
              {uploading && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="sign-doc-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il documento?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && <>Verrà eliminato definitivamente <strong>{deleteTarget.title}</strong> ({deleteTarget.document_id}) con tutte le sue versioni. L'operazione non è reversibile.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sign-doc-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="sign-doc-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
